import React from "react";
import { Box, Button, Typography } from "@mui/material";
import ErrorOutlineRoundedIcon from "@mui/icons-material/ErrorOutlineRounded";
import SearchOffRoundedIcon from "@mui/icons-material/SearchOffRounded";
import FilterAltOffRoundedIcon from "@mui/icons-material/FilterAltOffRounded";
import { ACCENT, surface, text } from "../theme";

interface ShellProps {
  icon: React.ReactNode;
  title: string;
  body: string;
  action?: { label: string; onClick: () => void };
}

function Shell({ icon, title, body, action }: ShellProps) {
  return (
    <Box
      role="status"
      sx={{
        gridColumn: "1 / -1",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        textAlign: "center",
        gap: 1.5,
        py: 8,
        px: 3,
        border: `1px dashed ${surface.border}`,
        borderRadius: 3,
      }}
    >
      <Box sx={{ color: text.tertiary, display: "flex" }}>{icon}</Box>
      <Typography variant="h3" sx={{ color: text.primary }}>
        {title}
      </Typography>
      <Typography variant="body2" sx={{ color: text.tertiary, maxWidth: 420 }}>
        {body}
      </Typography>
      {action && (
        <Button
          variant="contained"
          onClick={action.onClick}
          sx={{ mt: 1, bgcolor: ACCENT, color: "#1A1008", "&:hover": { bgcolor: "#D16A23" } }}
        >
          {action.label}
        </Button>
      )}
    </Box>
  );
}

export function ErrorState({
  message,
  onRetry,
}: {
  message: string;
  onRetry: () => void;
}) {
  return (
    <Shell
      icon={<ErrorOutlineRoundedIcon sx={{ fontSize: 40 }} />}
      title="Couldn't load the feed"
      body={message}
      action={{ label: "Try again", onClick: onRetry }}
    />
  );
}

export function NoResultsState({
  search,
  onClear,
}: {
  search: string;
  onClear: () => void;
}) {
  return (
    <Shell
      icon={<SearchOffRoundedIcon sx={{ fontSize: 40 }} />}
      title={`No stories match “${search}”`}
      body="Try a different term, or widen the selected sources."
      action={{ label: "Clear search", onClick: onClear }}
    />
  );
}

export function NoArticlesState({ onSelectAll }: { onSelectAll: () => void }) {
  return (
    <Shell
      icon={<SearchOffRoundedIcon sx={{ fontSize: 40 }} />}
      title="Nothing published here yet"
      body="The selected sources have no stories right now. Widening the selection should turn some up."
      action={{ label: "Select all sources", onClick: onSelectAll }}
    />
  );
}

export function NoSourcesState({ onSelectAll }: { onSelectAll: () => void }) {
  return (
    <Shell
      icon={<FilterAltOffRoundedIcon sx={{ fontSize: 40 }} />}
      title="No sources selected"
      body="Pick at least one source above to start reading."
      action={{ label: "Select all sources", onClick: onSelectAll }}
    />
  );
}
